import { Dayjs } from 'dayjs';
import { STRING_BOOLEAN_HASH } from '../constants/globals';
import { ChiroUpDayJsCommon } from '../constants/stringConstants';
import { CommunicationMethodTypes } from '../enums/CommunicationMethodTypes.enum';
import { createNeverNullDayjs } from '../functions/time';
import { TailwindColor } from './Color.type';
import type { PatientTransaction, PatientTransactionItemType } from './PatientTransaction.type';
import { AppointmentWorkflow } from './Workflow.type';

export interface AppointmentHistoryResult {
  totalCheckedIn: number;
  totalScheduledFuture: number;
  totalNoShow: number;
  totalCanceled: number;
  previousAppointment: {
    startTime: string | null;
    clinician: string | null;
    id: string | null;
  };
  nextAppointment: {
    startTime: string | null;
    clinician: string | null;
    id: string | null;
  };
}

export enum AppointmentStatuses {
  Scheduled = 'Scheduled',
  Canceled = 'Canceled',
  NoShow = 'No-Show',
  CheckedIn = 'Checked In',
  CheckedOut = 'Checked Out',
  Deleted = 'Deleted',
}

/**
 * [2026-01-10 by Brian]
 *   This is a helper function to determine if a transaction should have
 *   its tax rates updated. As long as it is scheduled it may be updated.
 *
 * @param transaction
 * @returns
 */
export const isaStatusToUpdateTaxRates = (transaction: PatientTransaction) => {
  const status = transaction?.status || null;
  if (!status) return false;

  return status !== AppointmentStatuses.CheckedIn && status !== AppointmentStatuses.CheckedOut;
};

export const DoNotCreateTransactionStatuses = {
  // [TODO TECH DEBT] It _may_ be necessary to have a transaction if an appointment
  //       is cancelled with a fee, so this allows it to be created. Unfortunately,
  //       it means it will just be deleted if there are no fee. Ideally, we would
  //       not create unnecessary transactions but the logic is very convoluted and
  //       this is a more reliable fix for now.
  // [AppointmentStatuses.Canceled]: true,
  [AppointmentStatuses.Deleted]: true,
};

export const AppointmentStatusOptions = Object.values(AppointmentStatuses).map(status => ({
  text: status,
  value: status,
}));

export type DoubleClickApptArgs = {
  day: string;
  index: number;
  startTimeInterval: number;
  userId: string;
  isRoom?: boolean;
};

export enum AppointmentTypes {
  NewPatient = 'New Patient',
  ExistingPatient = 'Existing Patient',
}

export enum BookingMethods {
  StaffAssisted = 'Staff Assisted',
  SelfScheduled = 'Self Scheduled',
}

export const disableStatusesInPast = [
  AppointmentStatuses.CheckedOut,
  AppointmentStatuses.CheckedIn,
  AppointmentStatuses.Canceled,
  AppointmentStatuses.NoShow,
];

export type AppointmentBase = {
  id: string;
  createdAt?: number;
  updatedAt?: number;
  appointmentWorkflow?: AppointmentWorkflow;
  assessmentCodes?: any[];
  bookingMethod?: BookingMethods;
  bottom?: number;
  cancellationReason?: string;
  checkInTime?: number;
  checkOutTime?: number;
  /**
   * UUID  = specific clinician assigned to this slot.
   * '-1'  = "Primary" — inherit the group appointment's clinician.
   * null  = no clinician (valid for room-only slots).
   *
   * The DB column is varchar(50) NULLABLE. '-1' is a sentinel string, not
   * a number. Do not convert it to null — that loses the user's intent.
   * Transaction/billing code resolves '-1'/null to the actual provider UUID
   * via resolveEffectiveClinician(); the Appointment row keeps the raw value.
   */
  clinicianId: string;
  clinicId: number;
  courtesyBilling?: boolean;
  createTrackCCSSubmission?: boolean;
  daysOfWeek?: string[];
  deleted?: boolean;
  deniedPatient?: boolean;
  disciplineId: number;
  displayValues: {
    patientName: string;
    clinicianName: string;
    treatmentName: string;
    disciplineName?: string;
    patientPhone?: string;
    patientEmail?: string;
    patientDOB?: string;
    hasActiveInsurance?: boolean;
    deniedPatient?: boolean;
    pendingPatient?: boolean;
    caseType?: number;
    nextRequestedEncounterDate?: string;
    roomName?: string;
    profilePhoto?: string;
  };
  // Consent is now managed at phone level in PhoneConsent table
  // Use PhoneConsentService.getPhoneConsent() with patient.phone to check consent
  duration: number;
  encounterSigned?: boolean;
  endRecurring?: any;
  recurringInterval?: number;
  groupId?: number;
  isPrimary?: boolean;
  hasBillingTransaction?: boolean;
  height?: number;
  isRoom?: boolean;
  left?: number;
  locationId: number;
  newPatient?: boolean;
  notes: string;
  patientId: string;
  patientName?: string;
  pendingPatient?: boolean;
  policies?: AppointmentInsuranceType[];
  recurringAppointmentId?: string;
  roomId?: number | null;
  services?: PatientTransactionItemType[];
  slots?: Appointment[];
  startTimeFormatted?: string;
  status: AppointmentStatuses;
  superBill?: boolean;
  surveySent?: string;
  timeOfDay?: string;
  top?: number;
  trackId?: number | null;
  treatmentId: number;
  treatmentName?: string;
  type?: AppointmentTypes;
  tz?: string;
  width?: number;
  availabilityMessage?: string | null;
};

export type Appointment = AppointmentBase & {
  startTime: number;
  suppressAutomatedSurveys?: boolean;
  suppressReminders?: boolean;
  getsReevalOrInterim?: boolean;
  getsACCS?: boolean;
  appointmentSurveys?: AppointmentSurvey[];
  surveyReminderSent?: boolean;
  surveyReminderDeliveryDate?: number;
  hasEncounter?: boolean | number;
};

export type AppointmentForUI = AppointmentBase & {
  inPast?: boolean;
  color?: TailwindColor;
  previous?: AppointmentForUI;
  latest?: AppointmentForUI;
  getsReevalOrInterim?: boolean;
  getsACCS?: boolean;
  startTime: Dayjs;
  surveys?: string[];
  hasPurchase?: boolean | number;
  hasEncounter?: boolean | number;
  appointmentSurveys?: AppointmentSurvey[];
  surveyReminderSent?: boolean;
  surveyReminderDeliveryDate?: number;
};

export type AppointmentSurvey = {
  surveyId: string;
  surveyType: 'consent' | 'survey' | 'request';
  sentAt: number | null;
  deliverAt: number;
  id: number;
  appointmentGroupId: number;
  surveyName: string;
};

export type AppointmentForEither = AppointmentBase & {
  startTime: number | Dayjs;
};

export type AppointmentInsuranceType = {
  ID: number;
  appointmentID?: string;
  billingPriority: number;
  clinicID: number;
  deductible: number;
  insuranceID: string;
  insuranceName: string;
  insuranceProgram?: string; // Used for claim creation.
  patientID?: string;
  disciplineID: number;
  coPay?: number;
  coInsurance?: number;
  payorID?: number;
  serviceAllowedAmounts?: {
    [key: string | number]: {
      payorID: number;
      allowedAmount?: number | string | null;
    };
  };
  inNetwork?: boolean;
  billable?: boolean;
  internallyBilled?: boolean;
  courtesyBilling?: boolean;
  updatePatientInsurance?: boolean;
  allowedAmount?: number | string | null;
  maxPerVisit?: number | string | null;
  payorId?: number;
  remainingNumberOfTreatments?: number;
  remainingNumberOfUses?: number;
  externalPayorId?: string;
};

export type ServicePayor = {
  ID?: number;
  allowedAmount?: number | string | null;
  code: string | number;
  payorID: number;
  insuranceID: string;
  billingRef: string;
  createdAt?: string;
  updatedAt?: string;
  deductible?: number;
  disciplineId?: number;
};

export type RecurringAppointmentData = {
  originalAppointmentUsedId?: string;
  duration?: number;
  patientId?: string;
  clinicianId?: string;
  treatmentId?: number;
  timestamps: {
    date: string;
    end: number;
    start: number;
  }[];
  displayValues?: {
    patientName: string;
    clinicianName: string;
    treatmentName: string;
  };
  locationId?: number;
};

export type CreateRecurringAppointmentsParams = {
  body: RecurringAppointmentsToSave;
  clinicId?: number;
  sessionId?: string;
  locationId?: number;
  notify?: boolean;
  originalAppointment?: AppointmentForUI;
};

export type RecurringAppointmentsToSave = {
  apptsToBook: ApptToBook[];
  patientId: string;
  locationId: number;
  // Recurring settings to be stored on each appointment for edit pre-population
  daysOfWeek?: string[];
  endRecurring?: number;
  recurringInterval?: number;
};

export type ApptToBook = {
  slots: RecurringAppointmentSlot[];
};

export type RecurringAppointmentSlot = {
  trackId?: number;
  clinicianId?: string;
  treatmentId?: number;
  start?: number;
  end?: number;
  roomId?: number;
  duration?: number;
  displayValues?: {
    patientName: string;
    clinicianName: string;
    treatmentName: string;
  };
};
export type RecurringAvailability = {
  treatmentId?: number;
  clinicianId?: string;
  patientId?: string;
  displayValues?: {
    patientName: string;
    clinicianName: string;
    treatmentName: string;
  };
  duration?: number;
  dataWithAllConflicts?: dataWithAllConflicts[];
};

export type dataWithAllConflicts = {
  id: number;
  conflicts: Appointment[];
  messages: {
    overrideMessage?: string;
    clinicLocationMessage?: string;
    providerMessage?: string;
  };
  slots?: {
    date?: string;
    end: number;
    start: number;
    treatmentId: number;
    clinicianId: string;
    roomId?: number;
    trackId?: number;
    roomName?: string;
    duration: number;
  }[];
  timestamp: { date: string; end: number; start: number };
};

export enum AppointmentTypes {
  Initial = 'initial',
  Interim = 'interim',
  Reevaluation = 'reevaluation',
}

export type AppointmentUser = {
  id: string;
  name: string;
  fname?: string;
  lname?: string;
  profileImage?: string;
  appointments: AppointmentForEither[];
  hours?: {
    start: number;
    end: number;
    maxPerSlot?: number;
    unlimited?: boolean;
  }[];
  color?: string;
  isRoom?: boolean;
  overrides?: {
    date: string;
    start: string;
    end: string;
    color: string;
    name?: string;
    recurringId?: number;
    clinicianId?: string;
  }[];
  normalHours?: {
    start: number;
    end: number;
  }[];
};

export type Appointments = {
  [key: string]: {
    [key: string]: AppointmentUser;
  };
};

export type AvailableSlotsResponse = {
  // Date
  [key: string]: {
    // User ID
    [key: string]: AppointmentForUI[];
  };
};

export type NoShowCancelFees = {
  ID: number;
  name: string;
  type: string;
  structure: string;
  value: number;
  clinicId: number;
  deleted: number;
};

export type AppointmentReminders = {
  id: number;
  clinicId: number;
  deleted: number;
  timing: number;
  timingUnit: 'minutes' | 'hours' | 'days';
  verbiage: string;
  spanishVerbiage: string | null;
  sendSurveys: boolean;
  treatments: number[];
  deliveryMethod: CommunicationMethodTypes;
};
export type ScheduledAppointmentReminders = {
  id: number;
  appointmentGroupId: number;
  appointmentId: string;
  appointmentRemindersId: number;
  deleted: number;
  deliverAt: number;
  sentAt: number | null;
  sendSurveys: boolean;
  deliveryMethod: CommunicationMethodTypes;
  verbiage: string;
  spanishVerbiage: string | null;
};

export type AppointmentSlot = {
  id: string;
  slotId: string;
  clinicId: number;
  patientId: string;
  clinicianId: string;
  disciplineId: number;
  treatmentId: number;
  duration: number;
  startTime: number;
};

/**
 * [Story # 7594941275] Super appointments.
 */
export type AppointmentGroupRow = {
  id: number;
  clinicId: number;
  type: number;
  patientId: string;
  clinicianId: string;
  disciplineId: number;
  treatmentId: number;
  notes?: string;
};
export const APPOINTMENT_GROUP_TABLE = 'AppointmentGroup';
export const APPOINTMENT_GROUP_ITEM_TABLE = 'AppointmentGroupItem';
export const APPOINTMENT_GROUP_LOG_TABLE = 'AppointmentGroupLog';

export const AppointmentGroupSchema = {
  columns: [
    { name: 'id', type: 'number', update: false },
    { name: 'clinicId', type: 'number', update: false },
    { name: 'type', type: 'number', update: false },
    { name: 'patientId', type: 'string', update: false },
    { name: 'clinicianId', type: 'string' },
    { name: 'disciplineId', type: 'number' },
    { name: 'treatmentId', type: 'number' },
    { name: 'notes', type: 'string' },
  ],
};
export const AppointmentGroupUpdateField = AppointmentGroupSchema.columns.reduce((a, col) => {
  a[col.name] = typeof col.update === 'boolean' ? col.update : true;

  return a;
}, {} as STRING_BOOLEAN_HASH);

export type AppointmentGroupItemRow = {
  clinicId: number;
  groupId: number;
  itemId: string;
};

export enum AppointmentGroupLogType {
  Created = 1,
  Updated = 2, // This is for updating the PRIMARY fields on AppointmentGroupRow [TODO: Need to implement logging for this.]
  SoftDeleted = 3,
  DeletedItem = 4,
  AddedItem = 5,
}

export const AppointmentGroupLogTypeDisplay = {
  [AppointmentGroupLogType.Created]: { tc: 'Created', lc: 'created' },
  [AppointmentGroupLogType.Updated]: { tc: 'Updated', lc: 'updated' },
  [AppointmentGroupLogType.SoftDeleted]: {
    tc: 'Soft deleted',
    lc: 'soft deleted',
  },
  [AppointmentGroupLogType.DeletedItem]: {
    tc: 'Deleted',
    lc: 'deleted item',
  },
  [AppointmentGroupLogType.AddedItem]: { tc: 'Added item', lc: 'added item' },
};

export const appointmentGroupLogDisplayText = ({
  appointment,
  type,
}: {
  appointment: Appointment;
  type: AppointmentGroupLogType;
}) => {
  if (!appointment || !type) return null;
  const startTimeDayJs = createNeverNullDayjs({
      datetime: appointment.startTime,
      timezone: appointment?.tz,
    }),
    clinicianName = appointment?.clinicianId
      ? (appointment?.displayValues?.clinicianName ?? `- ${appointment.clinicianId} -`)
      : null,
    treatmentName = appointment?.displayValues?.treatmentName ?? `- ${appointment.treatmentId} -`,
    roomName = appointment?.roomId
      ? (appointment?.displayValues?.roomName ?? `- ${appointment.roomId} -`)
      : null,
    startAt = `${startTimeDayJs.tz(appointment?.tz).format(ChiroUpDayJsCommon.format.time)}`;

  switch (type) {
    // Deleted time slot with ChiroUp Provider at 7:00pm for 60 minutes for treatment Orthopedic Massage
    case AppointmentGroupLogType.DeletedItem:
      return (
        [
          AppointmentGroupLogTypeDisplay[type].tc,
          appointment?.duration ?? '- na -',
          'minute time slot',
          clinicianName ? 'with' : 'in',
          clinicianName ?? roomName,
          'at',
          startAt,
          'for treatment',
          treatmentName,
        ].join(' ') + '.'
      );
    default:
      return null;
  }
};

export type AppointmentGroupLogRow = {
  id: number;
  clinicId: number;
  groupId: number;
  detail: any;
  type: AppointmentGroupLogType;
  timestamp: number;
};

export enum AppointmentGroupType {
  SuperAppointment = 1,
}
/*
 * /story/7594941275
 **/
